import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { PageHeader } from '@/layouts/AppLayout';

interface Hotel {
  id: string;
  name: string;
  city: string;
  state: string | null;
  country: string;
  contactPerson: string | null;
  contactEmail: string | null;
  retainerStatus: 'active' | 'paused' | 'terminated';
  retainerAmount: string | null;
  defaultCommissionPct: string | null;
}

const RETAINER_LABELS: Record<Hotel['retainerStatus'], string> = {
  active: 'Active',
  paused: 'Paused',
  terminated: 'Terminated',
};

export function HotelsListPage() {
  const qc = useQueryClient();

  const { data: hotels, isLoading } = useQuery({
    queryKey: ['hotels'],
    queryFn: () => api.get<Hotel[]>('/hotels'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/hotels/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['hotels'] });
      toast.success('Hotel deleted');
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Failed to delete hotel';
      toast.error(msg);
    },
  });

  const handleDelete = (hotel: Hotel) => {
    if (!confirm(`Delete "${hotel.name}"? This cannot be undone.`)) return;
    deleteMutation.mutate(hotel.id);
  };

  return (
    <div>
      <PageHeader
        title="Hotels"
        description="Properties under retainer management"
        action={
          <Button asChild>
            <Link to="/hotels/new">Add hotel</Link>
          </Button>
        }
      />
      <div className="p-6">
        {isLoading ? (
          <div className="flex justify-center py-16"><Spinner className="h-6 w-6" /></div>
        ) : !hotels?.length ? (
          <div className="rounded-lg border border-dashed border-neutral-300 py-16 text-center text-sm text-neutral-500">
            No hotels yet.{' '}
            <Link to="/hotels/new" className="text-tangerine-600 hover:underline">
              Add the first one.
            </Link>
          </div>
        ) : (
          <div className="rounded-lg border border-neutral-200 bg-white overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 bg-neutral-50">
                  <th className="px-4 py-3 text-left font-medium text-neutral-500">Name</th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-500">Location</th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-500">Contact</th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-500">Retainer</th>
                  <th className="px-4 py-3 text-left font-medium text-neutral-500">Commission</th>
                  <th className="px-4 py-3 text-right font-medium text-neutral-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {hotels.map((hotel) => (
                  <tr key={hotel.id} className="hover:bg-neutral-50 transition-colors">
                    <td className="px-4 py-3">
                      <Link
                        to={`/hotels/${hotel.id}`}
                        className="font-medium text-neutral-900 hover:text-tangerine-600"
                      >
                        {hotel.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-neutral-600">
                      {[hotel.city, hotel.state, hotel.country].filter(Boolean).join(', ')}
                    </td>
                    <td className="px-4 py-3 text-neutral-600">
                      <div>{hotel.contactPerson ?? '—'}</div>
                      {hotel.contactEmail && (
                        <div className="text-xs text-neutral-400">{hotel.contactEmail}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={hotel.retainerStatus}>
                        {RETAINER_LABELS[hotel.retainerStatus]}
                      </Badge>
                      {hotel.retainerAmount && (
                        <div className="mt-0.5 text-xs text-neutral-400">
                          ₹{Number(hotel.retainerAmount).toLocaleString('en-IN')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-neutral-600">
                      {hotel.defaultCommissionPct ? `${hotel.defaultCommissionPct}%` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={`/hotels/${hotel.id}/edit`}>Edit</Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-danger-700 hover:text-danger-700 hover:bg-danger-50"
                        onClick={() => handleDelete(hotel)}
                        disabled={deleteMutation.isPending}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
